import { getFirestore, doc, setDoc, getDoc, getDocs, collection, updateDoc, deleteField, deleteDoc, query, where } from 'firebase/firestore'
import { getAuth, signOut, updateProfile, signInAnonymously } from 'firebase/auth'
import { FitbitUserAPI } from '@/helpers/fitbit'
import { UserServiceAPI, ServiceAPI } from '@/helpers/service'
import { dateToday, getZone } from '@/helpers/time'
import Sleep from '@/models/sleep'
import Target from '@/models/target'
import Streak from '@/models/streak'
import Toolkit from '@/models/toolkit'
import Plan from '@/models/plan'
import { useSleepStore } from '@/stores/sleep'
import { useToolStore } from '@/stores/tool'
import { usePlanStore } from '@/stores/plan'
import { useUserStore } from '@/stores/user'

export default class User {
  constructor(id, name, email, goal, purpose, zone) {
    // Constructor should only be used internally by this class.
    if (!id) {
      throw new Error('User object requires an ID.')
    }
    this.id = id
    this.name = name
    this.email = email
    this.goal = goal
    this.purpose = purpose
    this.zone = zone
    // this.target is loaded asynchronously.
    this.target = undefined
    this._ref = this.ref
    this._auth = this.auth
    this._apiFitbit = this.apiFitbit
    this.apiService = new ServiceAPI()
    this.apiServiceUser = new UserServiceAPI()
  }

  static firestoreConverter() {
    return {
      toFirestore: (user) => {
        return {
          name: user.name,
          email: user.email || null,
          zone: user.zone,
        }
      },
      fromFirestore: (snapshot, options) => {
        const data = snapshot.data(options)

        return new User(
          snapshot.id,
          data.name,
          data.email || null,
          data.goal,
          data.purpose,
          data.zone,
        )
      }
    }
  }

  // Gets a specific user with ID, for admin use only.
  static async get(userID) {
    // Set admin in user store.
    const userStore = useUserStore()
    userStore.enableAdmin(userID)

    const user = new User(userID)
    await user.load()

    // Save to user store and disable data source link.
    user._hasDataSource = true
    userStore.update(user)

    return user
  }

  // Gets all users, for admin use only.
  static async getAll() {
    const snap = await getDocs(User.collection())
    const all = snap.docs.map(doc => doc.data())
    return await Promise.all(
      all.map(async u => {
        await u.load()
        return u
      })
    )
  }

  // Returns the user object of the authenticated user.
  // If no user is authenticated, then return null.
  // TODO: Rename to `current()`.
  static async getCurrentUser() {
    // Always reload the auth object when checking the current user.
    this.auth = getAuth()
    const auth = this.auth

    if (!auth.currentUser) {
      return null
    }
    const user = new User(auth.currentUser.uid, auth.currentUser.displayName, auth.currentUser.email)
    await user.load()

    // Save to user store.
    const userStore = useUserStore()
    userStore.update(user)

    return user
  }

  static collection() {
    return collection(getFirestore(), 'users').withConverter(User.firestoreConverter())
  }

  get ref() {
    if (!this._ref) {
      this._ref = doc(getFirestore(), 'users', this.id).withConverter(User.firestoreConverter())
    }
    return this._ref
  }

  get auth() {
    if (!this._auth) {
      this._auth = getAuth()
    }
    return this._auth
  }

  set auth(value) {
    this._auth = value
  }

  get apiFitbit() {
    if (!this._apiFitbit) {
      try {
        this._apiFitbit = new FitbitUserAPI()
      } catch (e) {
        console.error('Fitbit not connected because:', e)
        this._apiFitbit = null
      }
    }
    return this._apiFitbit
  }

  get hasDataSource() {
    if (this.apiFitbit || this._hasDataSource) {
      return true
    } else {
      return false
    }
  }

  static async create(name) {
    // Check a name is given.
    if (!name) {
      throw new Error('At minimum need a name to create a user.')
    }

    // Check that no user is currently authenticated.
    const current = await User.getCurrentUser()
    if (current) {
      throw new Error('Can not create a new user while already authenticated.')
    }

    // Create an anonymous user, authenticate them and save the details.
    await signInAnonymously(this.auth)
      .then(result => {
        return new User(result.user.uid, name)
      })
      .then(user => {
        return user.save()
      })
  }

  async load() {
    const snap = await getDoc(this.ref)
    const user = snap.data()
    // const user = await this.apiServiceUser.getUser()
    this.name = user?.name
    this.email = user?.email
    this.goal = user?.goal
    this.purpose = user?.purpose
    this.zone = user?.zone
    this.target = await this.getSleepTarget()
  }

  async save() {
    // Save to Firebase Auth service.
    await updateProfile(this.auth.currentUser, {
      displayName: this.name,
    })

    // Get user's zone.
    this.zone = getZone()

    // Save to user store.
    const userStore = useUserStore()
    userStore.update(this)

    // Save to Firestore DB.
    return setDoc(this._ref, this, {merge: true})
  }

  // Permanently clear all user data, but leaves authentication details, for
  // admin use only.
  async clear() {
    // Sleeps
    const sleeps = await Sleep.getSleepsAll(this.id)
    sleeps.forEach(async s => await s.delete())
    // TODO: Remove START to FINISH when `sleep_consistencies` is merged.
    // While consistency scores are separate, delete them too. Get all sleep
    // consistencies for user `this.id`, then delete them all.
    // START
    const userRef = doc(getFirestore(), 'users', this.id)
    const q = query(
      collection(getFirestore(), 'sleep_consistencies'),
      where('uuid', '==', userRef),
    )
    const snap = await getDocs(q)
    snap.docs.forEach(doc => deleteDoc(doc.ref))
    // FINISH

    // Plans
    const plans = await Plan.getByUser(this.id)
    plans.forEach(async p => await p.delete())

    // Streaks
    const streaks = await Streak.getByUser(this.id)
    streaks.forEach(async s => await s.delete())

    // Target
    if (this.target) {
      this.target.delete()
    }

    // User
    await updateDoc(this.ref, {
      name: null,
      email: null,
      goal: null,
      purpose: null,
      zone: null,
      level: null,
      tools: [],
      fitbitAccessToken: deleteField(),
      fitbitRefreshToken: deleteField(),
      fitbitUserId: deleteField(),
    })
  }

  // TODO:
  // Permanently deletes the user from the database, including authentication
  // details, for admin use only.
  async delete() {
    // this.clear()
    // TODO: Then remove authentication details.
  }

  updateGoal(goal) {
    this.goal = goal
    const userStore = useUserStore()
    userStore.update(this)
    return this.apiServiceUser.updateGoal(goal)
  }

  updatePurpose(purpose) {
    this.purpose = purpose
    const userStore = useUserStore()
    userStore.update(this)
    return this.apiServiceUser.updatePurpose(purpose)
  }

  // Only syncs sleep records from Fitbit, not the sleep targets.
  async syncFitbit() {
    return Sleep.syncFitbit(this.id, '2024-10-01')
      // If Fitbit is not authenticated, the sync will not be called with the
      // backend. This is the correct behaviour because streak days may be
      // marked as incompleted due to missing data.
      .then(async () => {
        const data = await this.apiServiceUser.sleepSync(dateToday())
        if (data?.updated.sync_streak) {
          Streak.get()
        }
        if (data?.updated.sync_plan) {
          const planStore = usePlanStore()
          planStore.reload()
        }
        // TODO: Depends on what page you're on when sync happens, but should
        // store date range in the store and reload score for those date ranges.
        // if (data.updated.sync_consistency) {}
      })
  }

  // Only syncs Fitbit sleep targets.
  async syncFitbitTarget() {
    return Target.syncFitbit()
  }

  async getSleepTarget() {
    return await Target.load()
  }

  async createSleepTarget(target) {
    return await Target.create(target.duration, target.bedTime, target.wakeTime)
  }

  async saveSleepTarget(target) {
    return await target.save()
  }

  async getSleeps(dateFrom, dateTo, mainOnly = false) {
    return await Sleep.getSleeps(this.id, dateFrom, dateTo, mainOnly)
  }

  async getSleepEfficiency(dateFrom, dateTo) {
    // Get the average efficiency across date range.
    const dailys = await this.getSleeps(dateFrom, dateTo)

    // Extract out just the efficiency.
    let efficiencies = []
    for (const daily of dailys) {
      let efficiency = await daily.efficiency
      efficiencies.push(efficiency)
    }

    const average = array => array.reduce((a, b) => a + b) / array.length
    return efficiencies.length ? average(efficiencies) : null
  }

  async getSleepConsistencyScore(dateFrom, dateTo) {
    const result = await this.apiServiceUser.getSleepConsistency(dateFrom, dateTo)
    const store = useSleepStore()
    store.updateConsistency(result)
    return result
  }

  async getSleepDuration(dateFrom, dateTo) {
    // Get the average duration across date range.
    const dailys = await this.getSleeps(dateFrom, dateTo)
    const target = await Target.load()

    // Extract out just the duration.
    let durations = []
    for (const daily of dailys) {
      let duration = daily.duration
      durations.push(duration)
    }

    const average = array => array.reduce((a, b) => a + b) / array.length
    const result = {
      score: durations.length ? average(durations) / target.duration : null,
      average: await this.apiServiceUser.getSleepDurationAverage(dateFrom, dateTo),
      range: null,
    }
    const store = useSleepStore()
    store.updateDuration(result)
    return result
  }

  async getSleepQualityScore(dateFrom, dateTo) {
    // Get the average quality across date range.
    const dailys = await this.getSleeps(dateFrom, dateTo)

    // Extract out just the quality.
    let qualities = []
    for (const daily of dailys) {
      let quality = daily.quality
      qualities.push(quality)
    }

    const average = array => array.reduce((a, b) => a + b) / array.length
    const result = {
      score: qualities.length ? average(qualities) : null,
      range: null,
    }
    const store = useSleepStore()
    store.updateQuality(result)
    return result
  }

  async logout() {
    const auth = getAuth()
    if (!auth.currentUser) {
      return
    }
    signOut(auth)
      .then(() => {
        this._apiFitbit.revokeTokens()
      })

    // Clear the user store.
    const userStore = useUserStore()
    userStore.clear()
  }

  async getAllTools() {
    // Get all tools.
    const tools = await this.apiService.getTools()
    let result = Toolkit.convert(tools)

    // Get all user unlocked tools and merge to the result.
    const unlocked = await this.apiServiceUser.getUserTools()
    // result.forEach(tool => {
      for (const unlock of Object.values(unlocked)) {
        const tool = result.tools.get(unlock.id)
        tool.unlockDate = unlock.unlocked_date
        tool.state = unlock.state
        // result.tools.set()
        // if (tool.id === unlock.id) {
        //   tool.unlockDate = unlock.unlocked_date
        //   tool.state = unlock.state
        // }
      }
    // })
    const store = useToolStore()
    store.updateAll(result)
    return result
  }

  async updateToolState(toolId, state) {
    const store = useToolStore()
    await this.apiServiceUser.updateUserTool(toolId, state)
    let result = store.getTool(toolId)
    result.state = state
    store.update(result)
    return result
  }

  async unlockTool() {
    let unlocked = await this.apiServiceUser.unlockUserTool()
    unlocked = Object.values(unlocked).pop()
    // Update store with updated `unlockDate` for that tool.
    const store = useToolStore()
    let result = store.getTool(unlocked.id)
    result.unlockDate = unlocked.unlocked_date
    store.update(result)
    return result
  }

  async getPlan() {
    const plan = await this.apiServiceUser.getPlan()
    if (!plan) return null
    const result = Plan.convert(plan)
    const store = usePlanStore()
    store.update(result)
    return result
  }

  async startPlan() {
    const plan = await this.apiServiceUser.createPlan()
    if (!plan) return null
    const result = Plan.convert(plan)
    const store = usePlanStore()
    store.update(result)
    // Since new plan unlocks a new tool, the tool store needs to be updated.
    // Just calling getAllTools() is enough.
    this.getAllTools()
    return plan
  }

  async finishPlan() {
    const planStore = usePlanStore()
    planStore.load()
    const planId = planStore.plan.id
    await this.apiServiceUser.stageAdvance(planId)
    const plan = await this.apiServiceUser.deactivatePlan(planId)
    if (!plan) return null
    const result = Plan.convert(plan)
    planStore.update(result)
    return plan
  }

  async retryStage() {
    const planStore = usePlanStore()
    planStore.load()
    const planId = planStore.plan.id
    const plan = await this.apiServiceUser.stageRetry(planId)
    if (!plan) return null
    const result = Plan.convert(plan)
    planStore.update(result)
    return plan
  }

  async advanceStage() {
    const planStore = usePlanStore()
    planStore.load()
    const planId = planStore.plan.id
    const plan = await this.apiServiceUser.stageAdvance(planId)
    if (!plan) return null
    const result = Plan.convert(plan)
    planStore.update(result)
    return plan
  }

}
